import { useDispatch, useSelector } from 'react-redux';
import { useMutation } from '@tanstack/react-query';
import { updateLeasingJourneyErrors } from 'state/slices/navigationSlice';
import { RootState } from 'state/store';

import { patchResponseForId } from 'services';
import {
  excludeHiddenGuarantorResponse,
  getSkippedHiddenGuarantorPageIndexes,
} from 'utils/display-logic/hiddenGuarantorResponses';

import { useGetPageData } from '../useGetPageData';

import { useEvaluateDisplayCondition } from './useEvaluateDisplayCondition';

export const useReconcileSkippedHiddenGuarantorPages = () => {
  const dispatch = useDispatch();
  const { form, activeResponse } = useSelector((state: RootState) => state.application);
  const evaluateDisplayCondition = useEvaluateDisplayCondition();
  const prepareData = useGetPageData();
  const { mutateAsync: updateResponseForId } = useMutation({
    mutationFn: patchResponseForId,
  });

  const reconcilePages = async (pageIndexes: number[], targetPageIndex: number): Promise<void> => {
    const [pageIndex, ...remainingPageIndexes] = pageIndexes;
    if (pageIndex === undefined) return;

    const page = form.pages[pageIndex];
    await updateResponseForId({
      responseId: activeResponse.responseId,
      responseData: {
        currentPageId: page.id,
        targetPageId: form.pages[targetPageIndex].id,
        responses: excludeHiddenGuarantorResponse(prepareData(pageIndex), true),
        errorCount: 0,
      },
    });
    dispatch(updateLeasingJourneyErrors({ pageIndex, count: 0 }));

    await reconcilePages(remainingPageIndexes, targetPageIndex);
  };

  return async (activePageIndex: number, targetPageIndex: number): Promise<void> => {
    if (activePageIndex === targetPageIndex) return;

    const pageIndexes = getSkippedHiddenGuarantorPageIndexes({
      form,
      activePageIndex,
      targetPageIndex,
      evaluateDisplayCondition,
    });

    await reconcilePages(pageIndexes, targetPageIndex);
  };
};
